import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  Alert,
  Checkbox,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography,
} from 'antd';
import { useQuery, useMutation } from '@apollo/client/react';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import { FEE_WAIVER_HISTORY, FEE_WAIVER_ELIGIBILITY } from 'queries/feeWaiver';
import { WAIVE_FEE } from 'mutations/transactions';
import { SIZE, STATUS } from 'utilities/constants';
import {
  formatDateToSubmit,
  isFeeWaiverScheduledDowntime,
} from 'utilities/datesAndTimes';
import { formatTransactionDescription } from 'utilities/helpers';

const OTHER = 'Other';
const WAIVER_REASON_OPTIONS = Object.freeze([
  {
    value: 'Hardship',
  },
  {
    value: 'Escalation',
  },
  {
    value: 'Natural Disaster',
  },
  {
    value: 'Incident',
  },
  {
    value: OTHER,
  },
]);

const FORM_FIELD = Object.freeze({
  REASON: 'reason',
  OTHER_REASON: 'otherReason',
  OVERRIDE: 'override',
});

const WAIVER_CHECK_CODES = {
  ACCOUNT_NOT_PAST_DUE: 'ACCOUNT_NOT_PAST_DUE',
  WAIVER_COUNT: 'WAIVER_COUNT',
};

const FeeWaiverModal = ({ transaction, hide, open, ...modalProps }) => {
  const { accountId, customerId } = useParams();
  const [waiverForm] = Form.useForm();
  const selectedReason = Form.useWatch(FORM_FIELD.REASON, waiverForm);

  const [showSuccess, setShowSuccess] = useState(false);
  const [feeWaiverFailure, setFeeWaiverFailure] = useState(undefined);

  const SCHEDULED_DOWNTIME_ERROR_MSG =
    'Fee waivers likely will not work between 8-8:15pm EST, please retry after 8:15pm EST.';

  const ERROR_MSG =
    'Failed to waive fee. Please retry or escalate to R2 to complete the process';

  // Really just checks if this fee has already been waived
  const eligibilityQuery = useQuery(FEE_WAIVER_ELIGIBILITY, {
    skip: !open || !transaction,
    variables: {
      accountId,
      postedDate: formatDateToSubmit(transaction?.postedAt),
      transactionId: transaction?.transactionId,
    },
  });

  const eligible = useMemo(() => {
    return eligibilityQuery.data?.adjustmentEligibility?.eligible
      ? true
      : false;
  }, [eligibilityQuery.data]);

  const feeWaiverHistoryQuery = useQuery(FEE_WAIVER_HISTORY, {
    variables: {
      accountId,
      customerId,
    },
  });

  const [overWaiverLimit, accountNotCurrent] = useMemo(() => {
    const historyEligibility =
      feeWaiverHistoryQuery.data?.account?.checkEligibility?.eligibility;

    let overLimit = false;
    let notCurrent = false;

    if (historyEligibility) {
      const countCheck = historyEligibility.reasons.find(
        (reason) => reason.code === WAIVER_CHECK_CODES.WAIVER_COUNT,
      );
      const notPastDueCheck = historyEligibility.reasons.find(
        (reason) => reason.code === WAIVER_CHECK_CODES.ACCOUNT_NOT_PAST_DUE,
      );

      overLimit = countCheck && !countCheck.pass;
      notCurrent = notPastDueCheck && !notPastDueCheck.pass;
    }
    return [overLimit, notCurrent];
  }, [feeWaiverHistoryQuery.data]);

  const [waiveFee, waiveFeeMutation] = useMutation(WAIVE_FEE, {
    refetchQueries: ['TransactionsQuery', 'FeeWaiverEligibility'],
  });

  const handleFeeWaiver = async () => {
    try {
      await waiverForm.validateFields();
    } catch {
      return;
    }

    setFeeWaiverFailure(undefined);

    const values = waiverForm.getFieldsValue();

    let updatedReason = values[FORM_FIELD.REASON];
    if (updatedReason === OTHER) {
      updatedReason = values[FORM_FIELD.OTHER_REASON];
    }

    if (values[FORM_FIELD.OVERRIDE]) {
      updatedReason = `OVERRIDE - ${updatedReason}`;
    }

    try {
      await waiveFee({
        variables: {
          accountId,
          customerId,
          postedDate: formatDateToSubmit(transaction.postedAt),
          transactionId: transaction.transactionId,
          updatedReason,
        },
      });
      setShowSuccess(true);
    } catch {
      setFeeWaiverFailure({ scheduled: isFeeWaiverScheduledDowntime() });
    }
  };

  const closeModal = () => {
    setShowSuccess(false);
    setFeeWaiverFailure(undefined);
    hide();
  };

  return (
    <Modal
      {...modalProps}
      data-testid="waive-fee-modal"
      open={open}
      width={640}
      title="Waive Fee"
      destroyOnHidden={true}
      onCancel={closeModal}
      onOk={handleFeeWaiver}
      okText="Waive Fee"
      okButtonProps={{
        'data-testid': 'waive-fee-button',
        disabled:
          !eligible ||
          eligibilityQuery.loading ||
          feeWaiverHistoryQuery.loading ||
          showSuccess,
      }}
      confirmLoading={waiveFeeMutation.loading}
    >
      <Form form={waiverForm} layout="vertical">
        <QueryBoundary
          query={eligibilityQuery}
          loadingMessage="Checking fee waiver eligibility"
          errorMessage="Failed to retrieve eligibility"
          loadingOverride={
            eligibilityQuery.loading || feeWaiverHistoryQuery.loading
          }
        >
          <Flex
            justify="center"
            align="center"
            vertical={true}
            style={{ position: 'relative', paddingTop: 'var(--spacing-lg)' }}
          >
            <Typography.Text
              strong
              style={{
                marginBottom: 'var(--spacing-lg)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {formatTransactionDescription(transaction)}
            </Typography.Text>

            {eligible ? (
              <>
                <Form.Item
                  name={FORM_FIELD.REASON}
                  label="Select a reason for this waiver"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a reason.',
                    },
                  ]}
                >
                  <Select
                    data-testid="reason-select"
                    options={WAIVER_REASON_OPTIONS}
                    style={{ width: '300px' }}
                  />
                </Form.Item>

                {selectedReason === OTHER ? (
                  <Form.Item
                    name={FORM_FIELD.OTHER_REASON}
                    label={'Enter "Other" reason'}
                    hidden={selectedReason !== OTHER}
                    rules={[
                      {
                        required: true,
                        message: 'Please elaborate on "Other" reason.',
                      },
                    ]}
                  >
                    <Input
                      data-testid="other-reason"
                      style={{ width: '300px' }}
                    />
                  </Form.Item>
                ) : undefined}

                {overWaiverLimit || accountNotCurrent ? (
                  <Alert
                    type="warning"
                    banner
                    showIcon={false}
                    style={{ marginBottom: 'var(--spacing-md)' }}
                    title={
                      <Form.Item
                        name={FORM_FIELD.OVERRIDE}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                        rules={[
                          {
                            validator: (_, checked) => {
                              return checked === true
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      'Must acknowledge fee waiver override.',
                                    ),
                                  );
                            },
                          },
                        ]}
                      >
                        <Checkbox data-testid="override-checkbox">
                          <Typography.Paragraph>
                            Checking this box will override the Fee Waiver
                            Policy - waiver restrictions below:{' '}
                          </Typography.Paragraph>

                          <ul
                            style={{
                              paddingInlineStart: 'var(--spacing-md)',
                            }}
                          >
                            {overWaiverLimit && (
                              <li>
                                This account is beyond its limit of 2 fee
                                waivers within the last 12 months. <br />
                              </li>
                            )}
                            {accountNotCurrent && (
                              <li>
                                This account is past due - Customer must make a
                                payment on the call, or have a payment scheduled
                                for today or tomorrow.
                              </li>
                            )}
                          </ul>
                        </Checkbox>
                      </Form.Item>
                    }
                  ></Alert>
                ) : undefined}
              </>
            ) : (
              <Alert
                data-testid="already-waived-message"
                banner
                type="warning"
                title="This fee has already been waived"
                style={{ marginBottom: 'var(--spacing)' }}
              />
            )}

            {showSuccess && (
              <SectionMessage
                data-testid="waive-fee-success"
                cover={true}
                status={STATUS.SUCCESS}
                size={SIZE.SM}
                text="Fee successfully waived."
              ></SectionMessage>
            )}

            {!!feeWaiverFailure && (
              <Alert
                banner
                data-testid="waive-fee-failure"
                type="error"
                title={
                  feeWaiverFailure.scheduled
                    ? SCHEDULED_DOWNTIME_ERROR_MSG
                    : ERROR_MSG
                }
                style={{ marginBottom: 'var(--spacing)' }}
              />
            )}
          </Flex>
        </QueryBoundary>
      </Form>
    </Modal>
  );
};

export default FeeWaiverModal;
